import React, { useEffect } from 'react';
import { Card, Form, Input, Button, Typography, Spin, message } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import styled, { keyframes } from 'styled-components';
import { useAppDispatch, useAppSelector } from '../Modules/Presentation/hooks';
import { selectAuth } from '../Modules/User/Presentation/selectors';
import { useNavigate } from 'react-router-dom';
import { loginThunk } from '../Modules/User/Presentation/actions';

const { Title, Text } = Typography;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const Wrapper = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  background: #0f1014;
  overflow-x: hidden;
  overflow-y: hidden;
  @media (max-width: 790px) {
    grid-template-columns: 1fr;
  }
`;

const LeftPanel = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: linear-gradient(135deg, #B03052 0%, #6f2b7c 35%, #1c3b6b 70%, #0f1014 100%);
  background-size: 200% 200%;
  animation: ${gradientShift} 12s ease infinite;
  @media (max-width: 790px) {
    display: none;
  }
`;

const LeftContent = styled.div`
  color: #fff;
  max-width: 520px;
  padding: 48px 32px;
`;

const RightPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  background: #ffffff;
  min-height: 100vh;
  min-height: 100dvh;
  @media (max-width: 480px) {
    padding: 24px 12px;
  }
`;

const StyledCard = styled(Card)`
  width: 100%;
  max-width: 420px;
  border-radius: 16px !important;
  border: 1px solid #f0f0f0;
  box-shadow: 0 18px 32px rgba(15, 16, 20, 0.08);
  margin: 0 12px;
  @media (max-width: 480px) {
    border-radius: 12px !important;
    box-shadow: 0 12px 24px rgba(15, 16, 20, 0.06);
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const Brand = styled(Title)`
  && {
    margin-bottom: 8px;
    color: #B03052;
  }
`;

const SubText = styled(Typography.Paragraph)`
  && {
    margin: 0;
    color: rgba(255, 255, 255, 0.85);
    font-size: 14px;
  }
`;

const LoginPage = () => {
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector(selectAuth);
  const navigate = useNavigate();

  useEffect(() => {
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, []);

  const onFinish = async (v: { username: string; password: string }) => {
    try {
      await dispatch(loginThunk(v)).unwrap();
      message.success('로그인이 완료되었습니다.');
      navigate('/slots', { replace: true });
    } catch {
      message.error('아이디 또는 비밀번호가 올바르지 않습니다.');
    }
  };

  return (
    <Wrapper>
      <LeftPanel>
        <LeftContent>
          <Brand level={2} style={{ color: '#fff' }}>Lift Reward</Brand>
          <SubText>
            데이터 기반으로 광고 슬롯을 정밀 관리하고, 효율을 극대화하세요.
          </SubText>
        </LeftContent>
      </LeftPanel>
      <RightPanel>
        <StyledCard bordered>
          <Header>
            <Title level={4} style={{ marginBottom: 4 }}>관리자 로그인</Title>
            <Typography.Text type="secondary">계정 정보를 입력해 주세요</Typography.Text>
          </Header>

          <Form layout="vertical" onFinish={onFinish} size="large">
            <Form.Item
              name="username"
              rules={[{ required: true, message: '아이디를 입력하세요' }]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="아이디"
                autoComplete="username"
                autoFocus
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: '비밀번호를 입력하세요' }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="비밀번호"
                autoComplete="current-password"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 8 }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                style={{ height: 46, borderRadius: 8 }}
                disabled={loading}
              >
                {loading ? <Spin size="small" /> : '로그인'}
              </Button>
            </Form.Item>
          </Form>
        </StyledCard>
      </RightPanel>
    </Wrapper>
  );
};

export default LoginPage;
